// تب پروفایل سازمانی SCR-STAFF-921 (US-IAM-020): نام، کد ملی با اعتبارسنجی (یکتا در مستأجر — AC-IAM-020.1 ← PHX-IAM-0401)،
// عنوان شغلی، کد پرسنلی و تاریخ شروع همکاری شمسی.
import { zodResolver } from '@hookform/resolvers/zod';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import {
  isApiError,
  unwrap,
  type MembershipView,
  type StaffProfileRequest,
  type StaffProfileView,
} from '@phoenix/api-client';
import { useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  applyServerErrors,
  ErrorState,
  JalaliDatePickerRHF,
  LoadingState,
  NationalIdFieldRHF,
  TextFieldRHF,
  useSaveShortcut,
  isValidNationalId,
  zRequiredText,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState, type FormEvent } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { WriteNotice } from '../settings/common';
import { iamKeys } from './common';

export const profileSchema = z.object({
  firstName: zRequiredText(100),
  lastName: zRequiredText(100),
  nationalId: z
    .string()
    .refine((value) => value === '' || isValidNationalId(value), { error: 'common:validation.national_id' }),
  jobTitle: z.string().max(100, { error: 'common:validation.too_long' }),
  personnelCode: z.string().max(32, { error: 'common:validation.too_long' }),
  startDate: z.string().nullable(),
});
type ProfileForm = z.input<typeof profileSchema>;

const FIELDS = ['firstName', 'lastName', 'nationalId', 'jobTitle', 'personnelCode', 'startDate'] as const;

function splitName(displayName: string): { firstName: string; lastName: string } {
  const [first, ...rest] = displayName.trim().split(/\s+/);
  return { firstName: first ?? '', lastName: rest.join(' ') };
}

export function ProfileTab({ member }: { member: MembershipView }) {
  const { t } = useTranslation('iam');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const write = useWriteAccess('iam.member.manage');
  const [saved, setSaved] = useState(false);
  const [submitError, setSubmitError] = useState<unknown>();
  const id = member.membershipId;

  const profile = useQuery({
    queryKey: iamKeys.profile(session?.tenantId, id),
    queryFn: async (): Promise<StaffProfileView | null> => {
      try {
        return await unwrap(
          tenantApi.GET('/api/v1/identity/memberships/{id}/profile', { params: { path: { id } } }),
        );
      } catch (error) {
        // بدون پروفایل (مالک پیش از تکمیل): فرم خالی با نام نمایشی.
        if (isApiError(error) && error.status === 404) {
          return null;
        }
        throw error;
      }
    },
  });

  const values = useMemo<ProfileForm>(() => {
    const data = profile.data;
    const names = splitName(member.displayName);
    return {
      firstName: data?.firstName ?? names.firstName,
      lastName: data?.lastName ?? names.lastName,
      nationalId: data?.nationalId ?? '',
      jobTitle: data?.jobTitle ?? '',
      personnelCode: data?.personnelCode ?? '',
      startDate: data?.startDate ?? null,
    };
  }, [profile.data, member.displayName]);

  const { control, handleSubmit, formState, setError } = useForm<ProfileForm>({
    resolver: zodResolver(profileSchema),
    values,
  });

  const save = useMutation({
    mutationFn: (body: StaffProfileRequest) =>
      unwrap(tenantApi.PUT('/api/v1/identity/memberships/{id}/profile', { params: { path: { id } }, body })),
    onMutate: () => {
      setSaved(false);
      setSubmitError(undefined);
    },
    onSuccess: (view) => {
      queryClient.setQueryData(iamKeys.profile(session?.tenantId, id), view);
      void queryClient.invalidateQueries({ queryKey: iamKeys.all(session?.tenantId) });
      setSaved(true);
    },
    onError: (error) => {
      const applied = applyServerErrors(error, setError, {
        fields: FIELDS,
        codeFields: { 'PHX-IAM-0401': 'nationalId' },
      });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  const submit = handleSubmit((form) =>
    save.mutate({
      firstName: form.firstName,
      lastName: form.lastName,
      nationalId: form.nationalId || null,
      jobTitle: form.jobTitle.trim() || null,
      personnelCode: form.personnelCode.trim() || null,
      startDate: form.startDate || null,
      departmentId: profile.data?.departmentId ?? null,
      photoDocumentId: profile.data?.photoDocumentId ?? null,
    }),
  );
  useSaveShortcut(() => void submit(), write.canWrite && !save.isPending);

  if (profile.isPending) {
    return <LoadingState rows={3} />;
  }
  if (profile.error) {
    return <ErrorState error={profile.error} onRetry={() => void profile.refetch()} />;
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="profile-tab">
      <Box
        component="form"
        noValidate
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          if (write.canWrite) {
            void submit();
          }
        }}
      >
        <Stack spacing={2}>
          <WriteNotice write={write} />
          {saved && (
            <Alert severity="success" data-testid="profile-saved">
              {t('iam:profile.saved')}
            </Alert>
          )}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextFieldRHF
              control={control}
              name="firstName"
              labelKey="iam:profile.first_name"
              required
              fullWidth
            />
            <TextFieldRHF
              control={control}
              name="lastName"
              labelKey="iam:profile.last_name"
              required
              fullWidth
            />
          </Stack>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <NationalIdFieldRHF
              control={control}
              name="nationalId"
              labelKey="iam:profile.national_id"
              helperKey="iam:profile.national_id_hint"
              fullWidth
            />
            <TextFieldRHF
              control={control}
              name="personnelCode"
              labelKey="iam:profile.personnel_code"
              fullWidth
            />
          </Stack>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextFieldRHF control={control} name="jobTitle" labelKey="iam:profile.job_title" fullWidth />
            <JalaliDatePickerRHF control={control} name="startDate" labelKey="iam:profile.start_date" />
          </Stack>
          {submitError ? <ErrorState error={submitError} compact /> : null}
          <Box>
            <Button
              type="submit"
              variant="contained"
              disabled={!write.canWrite || save.isPending || (profile.data !== null && !formState.isDirty)}
              data-testid="profile-submit"
            >
              {t('common:actions.save')}
            </Button>
          </Box>
        </Stack>
      </Box>
    </Paper>
  );
}
